import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { TimberKit, ProposalNodeSet, createDefaultNodeSet, MediaItem } from '../types';
import { calculateTotals } from '../utils/proposalCalculations';
import { getProposalRates, ProposalRateSettings, DEFAULT_PROPOSAL_RATES } from '../utils/appSettings';
import { AppHeader } from '../components/ui/AppHeader';
import StructureGridCard, { AddStructureCard } from '../components/proposals/StructureGridCard';
import StructureDetailModal from '../components/proposals/StructureDetailModal';
import { MediaPicker } from '../components/media';
import { useAuth } from '../utils/AuthContext';
import { ArrowLeft, Package, Tag, Calendar, Plus, Image, Lock, Unlock } from 'lucide-react';
import './KitBuilderPage.css';

interface KitState {
    nodeSets: ProposalNodeSet[];
    profitMargin: number;
}

const KitBuilderPage: React.FC = () => {
    const { kitId } = useParams<{ kitId?: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isNewKit = !kitId;

    // Kit metadata
    const [kitName, setKitName] = useState('');
    const [kitDescription, setKitDescription] = useState('');
    const [kitStatus, setKitStatus] = useState<'draft' | 'active' | 'archived'>('draft');

    // Sale configuration
    const [isOnSale, setIsOnSale] = useState(false);
    const [saleDiscountType, setSaleDiscountType] = useState<'percentage' | 'fixed'>('percentage');
    const [saleDiscount, setSaleDiscount] = useState(0);
    const [saleEndsAt, setSaleEndsAt] = useState<string>('');

    // Species pricing
    const [speciesPricing, setSpeciesPricing] = useState<{
        speciesName: string;
        timberTotal: number;
        billableTotal: number;
        structurePricing?: Array<{ nodeSetId: string; nodeSetName: string; cost: number }>;
    }[]>([]);

    // Selected species for pricing display
    const [selectedSpecies, setSelectedSpecies] = useState<string | null>(null);

    // Cover image and media picker
    const [coverImage, setCoverImage] = useState<string>('');
    const [showMediaPicker, setShowMediaPicker] = useState(false);

    // Lock for profit margin slider
    const [marginLocked, setMarginLocked] = useState(false);

    // Proposal state (reuses same structure as ProposalBuilderPage)
    const [state, setState] = useState<KitState>({
        nodeSets: [],
        profitMargin: 40,
    });

    const [loading, setLoading] = useState(!!kitId);
    const [saving, setSaving] = useState(false);
    const [selectedNodeSetId, setSelectedNodeSetId] = useState<string | null>(null);
    const [rates, setRates] = useState<ProposalRateSettings>(DEFAULT_PROPOSAL_RATES);
    const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Load rates on mount
    useEffect(() => {
        getProposalRates().then(setRates);
    }, []);

    // Load existing kit
    useEffect(() => {
        if (!kitId) {
            setLoading(false);
            return;
        }

        const loadKit = async () => {
            try {
                const kitRef = doc(db, 'timber_kits', kitId);
                const kitDoc = await getDoc(kitRef);

                if (kitDoc.exists()) {
                    const data = kitDoc.data() as TimberKit;
                    setKitName(data.name || '');
                    setKitDescription(data.description || '');
                    setKitStatus(data.status || 'draft');
                    setIsOnSale(data.isOnSale || false);
                    setSaleDiscountType(data.saleDiscountType || 'percentage');
                    setSaleDiscount(data.saleDiscount || 0);
                    setSaleEndsAt(data.saleEndsAt ? new Date(data.saleEndsAt).toISOString().split('T')[0] : '');
                    setSpeciesPricing(data.speciesPricing || []);
                    setCoverImage(data.coverImage || '');
                    setState({
                        nodeSets: data.nodeSets || [],
                        profitMargin: data.profitMargin || 40,
                    });
                }
            } catch (error) {
                console.error('Error loading kit:', error);
            } finally {
                setLoading(false);
            }
        };

        loadKit();
    }, [kitId]);

    // Calculate totals
    const totals = calculateTotals(state.nodeSets, state.profitMargin);

    // Save kit
    const saveKit = useCallback(async () => {
        if (!kitName.trim()) {
            alert('Please enter a kit name');
            return;
        }

        setSaving(true);
        try {
            const id = kitId || `kit-${Date.now()}`;
            const kitRef = doc(db, 'timber_kits', id);

            const kitDoc: TimberKit = {
                id,
                name: kitName.trim(),
                description: kitDescription.trim() || undefined,
                coverImage: coverImage || undefined,
                thumbnailUrl: coverImage || undefined, // Use cover image as thumbnail
                nodeSets: state.nodeSets,
                profitMargin: state.profitMargin,
                speciesPricing,
                isOnSale,
                saleDiscountType: isOnSale ? saleDiscountType : undefined,
                saleDiscount: isOnSale ? saleDiscount : undefined,
                saleEndsAt: saleEndsAt ? new Date(saleEndsAt) : undefined,
                ...totals,
                status: kitStatus,
                createdAt: isNewKit ? new Date() : (await getDoc(kitRef)).data()?.createdAt?.toDate?.() || new Date(),
                updatedAt: new Date(),
                createdBy: user?.email || 'system',
            };

            // Convert undefined to null for Firestore
            const cleanDoc = JSON.parse(JSON.stringify(kitDoc));

            await setDoc(kitRef, cleanDoc, { merge: true });

            if (isNewKit) {
                navigate(`/kit-builder/${id}`, { replace: true });
            }
        } catch (error) {
            console.error('Error saving kit:', error);
            alert('Failed to save kit. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [kitId, kitName, kitDescription, state, speciesPricing, isOnSale, saleDiscountType, saleDiscount, saleEndsAt, kitStatus, totals, isNewKit, navigate, user, coverImage]);

    // Autosave - debounced save when kit data changes
    useEffect(() => {
        // Don't autosave if loading or no name set
        if (loading || !kitName.trim()) return;

        // Clear any existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce autosave by 2 seconds
        saveTimeoutRef.current = setTimeout(() => {
            saveKit();
        }, 2000);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [kitName, kitDescription, state, speciesPricing, isOnSale, saleDiscountType, saleDiscount, saleEndsAt, kitStatus, loading, saveKit, coverImage]);

    // Add structure
    const handleAddStructure = useCallback(() => {
        const newNodeSet = createDefaultNodeSet(`Structure ${state.nodeSets.length + 1}`);
        setState(prev => ({
            ...prev,
            nodeSets: [...prev.nodeSets, newNodeSet],
        }));
    }, [state.nodeSets.length]);

    // Update node set
    const handleNodeSetUpdate = useCallback((nodeSetId: string, updates: Partial<ProposalNodeSet>) => {
        setState(prev => ({
            ...prev,
            nodeSets: prev.nodeSets.map(ns =>
                ns.id === nodeSetId ? { ...ns, ...updates } : ns
            ),
        }));
    }, []);

    // Delete node set
    const handleDeleteNodeSet = useCallback((nodeSetId: string) => {
        setState(prev => ({
            ...prev,
            nodeSets: prev.nodeSets.filter(ns => ns.id !== nodeSetId),
        }));
        setSelectedNodeSetId(null);
    }, []);

    // Rename node set
    const handleRenameNodeSet = useCallback((newName: string) => {
        if (!selectedNodeSetId) return;
        setState(prev => ({
            ...prev,
            nodeSets: prev.nodeSets.map(ns =>
                ns.id === selectedNodeSetId ? { ...ns, name: newName } : ns
            ),
        }));
    }, [selectedNodeSetId]);

    // Calculate non-timber costs (shared across all species)
    // For kit builder, we don't include install/travel - those are quoted at client level
    // Non-timber costs = design + production + freight + overhead (everything except timber materials and install/travel)
    const nonTimberCosts = React.useMemo(() => {
        let total = 0;
        state.nodeSets.forEach(ns => {
            total += ns.design.total;
            total += ns.production.total;
            total += ns.freight.total;
            total += ns.overhead.total;
        });
        return total;
    }, [state.nodeSets]);

    // Auto-calculate species pricing from species configurations
    // Each structure is calculated independently with profit margin, then summed for kit total
    // This allows individual structures to be dropped while maintaining correct pricing
    const calculatedSpeciesPricing = React.useMemo(() => {
        // Apply profit margin using true margin formula (same as proposalCalculations.ts)
        const applyMargin = (cost: number) => {
            if (state.profitMargin >= 100) {
                return cost * 10; // Cap at 10x for safety
            }
            return cost / (1 - state.profitMargin / 100);
        };

        // Build a map of species -> { totalCost, structurePricing[] }
        const speciesMap = new Map<string, {
            timberTotal: number;
            kitTotal: number;
            structurePricing: Array<{ nodeSetId: string; nodeSetName: string; cost: number }>;
        }>();

        state.nodeSets.forEach(ns => {
            // Calculate this structure's non-timber costs
            // Note: materials.total includes timber, so subtract timberTotal to get just non-timber materials (peg package, fasteners, post bases)
            const nonTimberMaterialsCost = (ns.materials?.total || 0) - (ns.materials?.timberTotal || 0);
            const structureNonTimberCost = (ns.design?.total || 0) +
                nonTimberMaterialsCost +
                (ns.production?.total || 0) +
                (ns.freight?.total || 0) +
                (ns.overhead?.total || 0);

            // Get default species from first timber item
            const defaultSpecies = ns.materials?.timberList?.[0]?.species || 'Default';
            const defaultTimberTotal = ns.materials?.timberTotal || 0;

            // Calculate this structure's cost with margin applied
            const defaultStructureCost = Math.round(applyMargin(defaultTimberTotal + structureNonTimberCost));

            // Add to species map
            if (!speciesMap.has(defaultSpecies)) {
                speciesMap.set(defaultSpecies, { timberTotal: 0, kitTotal: 0, structurePricing: [] });
            }
            const defaultEntry = speciesMap.get(defaultSpecies)!;
            defaultEntry.timberTotal += defaultTimberTotal;
            defaultEntry.kitTotal += defaultStructureCost;
            defaultEntry.structurePricing.push({
                nodeSetId: ns.id,
                nodeSetName: ns.name || `Structure ${state.nodeSets.indexOf(ns) + 1}`,
                cost: defaultStructureCost
            });

            // Handle alternate species if multi-species is enabled
            if (ns.materials?.quoteMultipleSpecies && ns.materials?.alternateSpecies) {
                ns.materials.alternateSpecies.forEach(altSpecies => {
                    const altTimberTotal = altSpecies.timberTotal || 0;
                    const altStructureCost = Math.round(applyMargin(altTimberTotal + structureNonTimberCost));

                    if (!speciesMap.has(altSpecies.speciesName)) {
                        speciesMap.set(altSpecies.speciesName, { timberTotal: 0, kitTotal: 0, structurePricing: [] });
                    }
                    const altEntry = speciesMap.get(altSpecies.speciesName)!;
                    altEntry.timberTotal += altTimberTotal;
                    altEntry.kitTotal += altStructureCost;
                    altEntry.structurePricing.push({
                        nodeSetId: ns.id,
                        nodeSetName: ns.name || `Structure ${state.nodeSets.indexOf(ns) + 1}`,
                        cost: altStructureCost
                    });
                });
            }
        });

        // Convert map to array and filter out 'Default' placeholder
        return Array.from(speciesMap.entries())
            .filter(([name]) => name !== 'Default')
            .map(([speciesName, data]) => ({
                speciesName,
                timberTotal: data.timberTotal,
                kitTotal: data.kitTotal,
                structurePricing: data.structurePricing
            }))
            .sort((a, b) => a.speciesName.localeCompare(b.speciesName));
    }, [state.nodeSets, state.profitMargin]);

    // Sync speciesPricing state with calculated values and auto-select first species
    React.useEffect(() => {
        if (calculatedSpeciesPricing.length === 0) return;

        setSpeciesPricing(calculatedSpeciesPricing.map(sp => ({
            speciesName: sp.speciesName,
            timberTotal: sp.timberTotal,
            billableTotal: sp.kitTotal,
            structurePricing: sp.structurePricing
        })));

        // Auto-select first species if none selected
        if (!selectedSpecies && calculatedSpeciesPricing.length > 0) {
            setSelectedSpecies(calculatedSpeciesPricing[0].speciesName);
        }
    }, [calculatedSpeciesPricing, selectedSpecies]);

    // Handle media picker selection for cover image
    const handleCoverImageSelect = (item: MediaItem) => {
        setCoverImage(item.url);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    if (loading) {
        return (
            <div className="kit-builder-page">
                <AppHeader />
                <div className="kit-builder-loading">
                    <div className="loading-spinner" />
                    <p>Loading kit...</p>
                </div>
            </div>
        );
    }

    const selectedNodeSet = state.nodeSets.find(ns => ns.id === selectedNodeSetId);

    return (
        <div className="kit-builder-page">
            <AppHeader />

            <div className="kit-builder-content">
                {/* Header */}
                <div className="kit-builder-header">
                    <div className="header-left">
                        <button className="back-btn" onClick={() => navigate('/kit-management')}>
                            <ArrowLeft size={20} />
                        </button>
                        <div className="header-info">
                            <h1>
                                <Package size={24} />
                                {isNewKit ? 'Create New Kit' : 'Edit Kit'}
                            </h1>
                        </div>
                    </div>

                    <div className="header-actions">
                        <select
                            className="status-select"
                            value={kitStatus}
                            onChange={(e) => setKitStatus(e.target.value as 'draft' | 'active' | 'archived')}
                        >
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                </div>

                {/* Main content */}
                <div className="kit-builder-main">
                    {/* Left sidebar - Kit info */}
                    <div className="kit-sidebar">
                        {/* Basic Info */}
                        <div className="sidebar-section">
                            <h3>Kit Details</h3>

                            <div className="form-group">
                                <label>Kit Name *</label>
                                <input
                                    type="text"
                                    value={kitName}
                                    onChange={(e) => setKitName(e.target.value)}
                                    placeholder="e.g., 16x20 Pavilion"
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={kitDescription}
                                    onChange={(e) => setKitDescription(e.target.value)}
                                    placeholder="Brief description of the kit..."
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <div className="profit-slider-container">
                                    <div className="profit-slider-label">
                                        <span>Profit Margin</span>
                                        <div className="profit-input-group">
                                            <input
                                                type="number"
                                                min="0"
                                                max="80"
                                                step="0.1"
                                                value={state.profitMargin}
                                                disabled={marginLocked}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value);
                                                    if (!isNaN(val) && val >= 0 && val <= 80) {
                                                        setState(prev => ({ ...prev, profitMargin: val }));
                                                    }
                                                }}
                                                className="profit-input"
                                            />
                                            <span className="profit-input-percent">%</span>
                                            <button
                                                type="button"
                                                className={`margin-lock-btn ${marginLocked ? 'locked' : ''}`}
                                                onClick={() => setMarginLocked(!marginLocked)}
                                                title={marginLocked ? 'Unlock profit margin' : 'Lock profit margin'}
                                            >
                                                {marginLocked ? <Lock size={14} /> : <Unlock size={14} />}
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        type="range"
                                        value={state.profitMargin}
                                        disabled={marginLocked}
                                        onChange={(e) => setState(prev => ({ ...prev, profitMargin: Number(e.target.value) }))}
                                        min="0"
                                        max="80"
                                        step="0.5"
                                        className={`profit-slider ${marginLocked ? 'locked' : ''}`}
                                        style={{ '--slider-progress': `${(state.profitMargin / 80) * 100}%` } as React.CSSProperties}
                                    />
                                </div>
                            </div>

                            {/* Cover Image */}
                            <div className="form-group">
                                <label>Cover Image</label>
                                <div className="cover-image-upload">
                                    {coverImage ? (
                                        <div className="cover-image-preview">
                                            <img src={coverImage} alt="Kit cover" />
                                            <button
                                                className="remove-image-btn"
                                                onClick={() => setCoverImage('')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="upload-image-btn"
                                            onClick={() => setShowMediaPicker(true)}
                                        >
                                            <Image size={18} />
                                            Select from Media
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sale Configuration */}
                        <div className="sidebar-section">
                            <h3>
                                <Tag size={18} />
                                Sale / Discount
                            </h3>

                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={isOnSale}
                                        onChange={(e) => setIsOnSale(e.target.checked)}
                                    />
                                    Enable Sale Pricing
                                </label>
                            </div>

                            {isOnSale && (
                                <>
                                    <div className="form-group">
                                        <label>Discount Type</label>
                                        <div className="toggle-buttons">
                                            <button
                                                type="button"
                                                className={`toggle-btn ${saleDiscountType === 'percentage' ? 'active' : ''}`}
                                                onClick={() => setSaleDiscountType('percentage')}
                                            >
                                                %
                                                Percentage
                                            </button>
                                            <button
                                                type="button"
                                                className={`toggle-btn ${saleDiscountType === 'fixed' ? 'active' : ''}`}
                                                onClick={() => setSaleDiscountType('fixed')}
                                            >
                                                $
                                                Fixed Amount
                                            </button>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>
                                            {saleDiscountType === 'percentage' ? '%' : '$'}
                                            {saleDiscountType === 'percentage' ? ' Discount' : ' Discount Amount'}
                                        </label>
                                        <input
                                            type="number"
                                            value={saleDiscount}
                                            onChange={(e) => setSaleDiscount(Number(e.target.value))}
                                            min="0"
                                            max={saleDiscountType === 'percentage' ? 100 : undefined}
                                            placeholder={saleDiscountType === 'percentage' ? 'e.g., 15' : 'e.g., 500'}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>
                                            <Calendar size={14} />
                                            Sale Ends
                                        </label>
                                        <input
                                            type="date"
                                            value={saleEndsAt}
                                            onChange={(e) => setSaleEndsAt(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Kit Pricing - show kit price per species with sale pricing */}
                        <div className="sidebar-section totals-section">
                            <h3>
                                Kit Pricing
                                {isOnSale && saleEndsAt && (
                                    <span className="sale-badge">SALE ends {new Date(saleEndsAt).toLocaleDateString()}</span>
                                )}
                            </h3>
                            <div className="totals-grid">
                                {speciesPricing.map((sp) => {
                                    const originalPrice = sp.billableTotal;
                                    const salePrice = isOnSale
                                        ? saleDiscountType === 'percentage'
                                            ? originalPrice * (1 - saleDiscount / 100)
                                            : originalPrice - saleDiscount
                                        : originalPrice;

                                    return (
                                        <div key={sp.speciesName} className={`total-row highlight ${isOnSale ? 'has-sale' : ''}`}>
                                            <span>{sp.speciesName}</span>
                                            <span className="price-display">
                                                {isOnSale ? (
                                                    <>
                                                        <span className="original-price">{formatCurrency(originalPrice)}</span>
                                                        <span className="sale-price">{formatCurrency(salePrice)}</span>
                                                    </>
                                                ) : (
                                                    formatCurrency(originalPrice)
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right - Structure grid */}
                    <div className="kit-structures">
                        <div className="structures-header">
                            <h2>Structures</h2>
                            <span className="structure-count">{state.nodeSets.length} structure(s)</span>
                        </div>

                        <div className="structures-grid">
                            {state.nodeSets.map((nodeSet) => (
                                <StructureGridCard
                                    key={nodeSet.id}
                                    nodeSet={nodeSet}
                                    onClick={() => setSelectedNodeSetId(nodeSet.id)}
                                    hidePrice={true}
                                    hideInstall={true}
                                />
                            ))}
                            <AddStructureCard onClick={handleAddStructure} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Structure detail modal */}
            {
                selectedNodeSet && (
                    <StructureDetailModal
                        nodeSet={selectedNodeSet}
                        allNodeSets={state.nodeSets}
                        rates={rates}
                        profitMargin={state.profitMargin}
                        onClose={() => setSelectedNodeSetId(null)}
                        onUpdate={(updates) => handleNodeSetUpdate(selectedNodeSet.id, updates)}
                        onDelete={() => handleDeleteNodeSet(selectedNodeSet.id)}
                        onRename={handleRenameNodeSet}
                        hideInstallTravel={true}
                        hideStructureFreightAndOverhead={false}
                    />
                )
            }

            {/* Media Picker for cover image */}
            <MediaPicker
                isOpen={showMediaPicker}
                onClose={() => setShowMediaPicker(false)}
                onSelect={handleCoverImageSelect}
                allowedTypes={['image']}
                title="Select Kit Cover Image"
            />
        </div >
    );
};

export default KitBuilderPage;
